import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { X, Send, MoreVertical, Phone, Video, MessageCircle } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { createPortal } from 'react-dom';

// Styled Components for Facebook-like interface
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90vw;
  max-width: 800px;
  height: 80vh;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  transition: transform 0.3s ease;
`;

const ChatHeader = styled.div`
  background: #1877f2;
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CompetitorAvatar = styled.div<{ isOnline: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.isOnline ? '#4caf50' : '#9e9e9e'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.isOnline ? '#4caf50' : '#9e9e9e'};
    border: 2px solid white;
  }
`;

const CompetitorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompetitorName = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const CompetitorStatus = styled.div`
  font-size: 12px;
  opacity: 0.9;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ChatBody = styled.div`
  flex: 1;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div<{ isSender: boolean }>`
  max-width: 70%;
  align-self: ${({ isSender }) => isSender ? 'flex-end' : 'flex-start'};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MessageContent = styled.div<{ isSender: boolean }>`
  background: ${({ isSender }) => isSender ? '#1877f2' : 'white'};
  color: ${({ isSender }) => isSender ? 'white' : '#1c1e21'};
  padding: 12px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  
  ${({ isSender }) => isSender && `
    border-bottom-right-radius: 4px;
  `}
  
  ${({ isSender }) => !isSender && `
    border-bottom-left-radius: 4px;
  `}
`;

const MessageTime = styled.div<{ isSender: boolean }>`
  font-size: 11px;
  color: #65676b;
  align-self: ${({ isSender }) => isSender ? 'flex-end' : 'flex-start'};
  margin: 0 4px;
`;

const MessageStatus = styled.div<{ isSender: boolean }>`
  font-size: 11px;
  color: #65676b;
  align-self: ${({ isSender }) => isSender ? 'flex-end' : 'flex-start'};
  margin: 0 4px;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ChatInputSection = styled.div`
  background: white;
  padding: 16px 20px;
  border-top: 1px solid #e4e6ea;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const MessageInput = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 12px 16px;
  border: 1px solid #e4e6ea;
  border-radius: 20px;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #1877f2;
  }
  
  &::placeholder {
    color: #65676b;
  }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionIconButton = styled.button`
  background: none;
  border: none;
  color: #65676b;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f0f2f5;
    color: #1877f2;
  }
`;

const SendButton = styled.button<{ hasMessage: boolean }>`
  background: ${({ hasMessage }) => hasMessage ? '#1877f2' : '#e4e6ea'};
  color: ${({ hasMessage }) => hasMessage ? 'white' : '#65676b'};
  border: none;
  padding: 12px;
  border-radius: 50%;
  cursor: ${({ hasMessage }) => hasMessage ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${({ hasMessage }) => hasMessage ? '#166fe5' : '#e4e6ea'};
    transform: ${({ hasMessage }) => hasMessage ? 'scale(1.05)' : 'none'};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #65676b;
  text-align: center;
  gap: 16px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  opacity: 0.5;
`;

const EmptyStateText = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const EmptyStateSubtext = styled.div`
  font-size: 14px;
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 0 20px;
  font-size: 14px;
  text-align: center;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  color: #65676b;
  font-size: 12px;
  font-style: italic;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 2px;
  
  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #65676b;
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
  
  @keyframes typing {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

interface Message {
  _id: string;
  content: string;
  sender: {
    id: string;
  };
  createdAt: string;
}

interface CompetitorChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: {
    id: string;
    name: string;
    role: string;
  };
  competitor: {
    id: string;
    name: string;
    isOnline: boolean;
    avatar: string;
    profilePicture?: string;
  };
  conversationId?: string;
}

const CompetitorChatModal: React.FC<CompetitorChatModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  competitor,
  conversationId
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentConvId, setCurrentConvId] = useState<string | null>(conversationId || null);
  const { token } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Handle modal visibility
  useEffect(() => {
    if (isOpen && overlayRef.current) {
      requestAnimationFrame(() => {
        if (overlayRef.current) {
          overlayRef.current.classList.add('visible');
        }
      });
    } else if (overlayRef.current) {
      overlayRef.current.classList.remove('visible');
    }
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && 
          overlayRef.current && overlayRef.current === event.target) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Initialize or get conversation
  useEffect(() => {
    if (isOpen && token && currentUser.id && competitor.id) {
      initializeConversation();
    }
  }, [isOpen, token, currentUser.id, competitor.id]);

  const initializeConversation = async () => {
    try {
      setError('');
      
      if (currentConvId) {
        // Use existing conversation
        return;
      }

      // Create new competitor conversation
      const response = await api.post('/messages/conversations', {
        participants: [currentUser.id, competitor.id],
        type: 'competitor'
      });

      if (response.data?.success) {
        setCurrentConvId(response.data.data._id);
      } else {
        throw new Error('Failed to create conversation');
      }
    } catch (err: any) {
      console.error('Error initializing conversation:', err);
      setError('Failed to initialize conversation. Please try again.');
    }
  };

  // Fetch messages
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;
    let isPolling = true;

    const fetchMessages = async () => {
      if (!isPolling || !currentConvId) return;
      
      try {
        const response = await api.get(`/messages/${currentConvId}`);
        const data = response.data;
        
        if (data.success && Array.isArray(data.data)) {
          const sortedMessages = data.data.sort((a: Message, b: Message) => 
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          setMessages(sortedMessages);
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages');
      }
    };

    if (isOpen && token && currentConvId) {
      fetchMessages();
      pollInterval = setInterval(fetchMessages, 3000);
    }

    return () => {
      isPolling = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isOpen, currentConvId, token]);

  // Mark conversation as read
  useEffect(() => {
    if (isOpen && currentConvId && currentUser.id && token) {
      api.post('/messages/mark-conversation-as-read', { 
        conversationId: currentConvId, 
        userId: currentUser.id 
      }).catch(err => {
        console.error('Failed to mark conversation as read', err);
      });
    }
  }, [isOpen, currentConvId, currentUser.id, token]);

  const handleSend = async () => {
    if (!message.trim() || !token || !currentConvId) return;

    setIsSending(true);
    setError('');

    try {
      const messageData = {
        conversationId: currentConvId,
        senderId: currentUser.id,
        receiverId: competitor.id,
        text: message.trim(),
        senderRole: currentUser.role,
        receiverRole: 'student', // Competitors are students
        senderName: currentUser.name,
        receiverName: competitor.name
      };

      const response = await api.post('/messages/send', messageData);

      if (response.data?.success) {
        setMessages(prev => [...prev, response.data.data]);
        setMessage('');
        // Focus back to input after sending
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        throw new Error(response.data?.error || 'Failed to send message');
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTyping = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    // Simulate typing indicator
    if (e.target.value.length > 0 && !isTyping) {
      setIsTyping(true);
      setTimeout(() => setIsTyping(false), 1000);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    (
      <ModalOverlay ref={overlayRef}>
        <ModalContent ref={modalRef}>
          <ChatHeader>
            <HeaderLeft>
              <CompetitorAvatar isOnline={competitor.isOnline}>
                {(competitor.profilePicture && (competitor.profilePicture.startsWith('http') || competitor.profilePicture.startsWith('data:'))) || 
                 (competitor.avatar && (competitor.avatar.startsWith('http') || competitor.avatar.startsWith('data:'))) ? (
                  <img 
                    src={competitor.profilePicture || competitor.avatar} 
                    alt={competitor.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = competitor.name.substring(0, 2).toUpperCase();
                      }
                    }}
                  />
                ) : (
                  competitor.name.substring(0, 2).toUpperCase()
                )}
              </CompetitorAvatar>
              <CompetitorInfo>
                <CompetitorName>{competitor.name}</CompetitorName>
                <CompetitorStatus>
                  {/* Status indicator only - no text */}
                </CompetitorStatus>
              </CompetitorInfo>
            </HeaderLeft>
            
            <HeaderActions>
              <ActionButton title="Voice Call">
                <Phone size={18} />
              </ActionButton>
              <ActionButton title="Video Call">
                <Video size={18} />
              </ActionButton>
              <ActionButton title="More Options">
                <MoreVertical size={18} />
              </ActionButton>
              <CloseButton onClick={onClose}>
                <X size={18} />
              </CloseButton>
            </HeaderActions>
          </ChatHeader>

          <ChatBody>
            <MessagesContainer ref={messageContainerRef}>
              {messages.length === 0 ? (
                <EmptyState>
                  <EmptyStateIcon>💬</EmptyStateIcon>
                  <EmptyStateText>No messages yet</EmptyStateText>
                  <EmptyStateSubtext>
                    Start a conversation with {competitor.name}
                  </EmptyStateSubtext>
                </EmptyState>
              ) : (
                messages.map(msg => (
                  <MessageBubble key={msg._id} isSender={msg.sender?.id === currentUser.id}>
                    <MessageContent isSender={msg.sender?.id === currentUser.id}>
                      {msg.content}
                    </MessageContent>
                    <MessageTime isSender={msg.sender?.id === currentUser.id}>
                      {new Date(msg.createdAt).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </MessageTime>
                    {msg.sender?.id === currentUser.id && (
                      <MessageStatus isSender={true}>
                        ✓✓
                      </MessageStatus>
                    )}
                  </MessageBubble>
                ))
              )}
              
              {isTyping && (
                <TypingIndicator>
                  <span>{competitor.name} is typing</span>
                  <TypingDots>
                    <span></span>
                    <span></span>
                    <span></span>
                  </TypingDots>
                </TypingIndicator>
              )}
            </MessagesContainer>
          </ChatBody>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ChatInputSection>
            <InputRow>
              <MessageInput
                ref={inputRef}
                value={message}
                onChange={handleTyping}
                placeholder={`Message ${competitor.name}...`}
                onKeyDown={handleKeyDown}
                rows={1}
              />
                              <InputActions>
                  <ActionIconButton title="Emoji">
                    😊
                  </ActionIconButton>
                  <ActionIconButton title="Attach File">
                    📎
                  </ActionIconButton>
                  <ActionIconButton title="Voice Message">
                    🎤
                  </ActionIconButton>
                  <SendButton 
                  hasMessage={!!message.trim()}
                  onClick={handleSend}
                  disabled={!message.trim() || isSending}
                >
                  <Send size={18} />
                </SendButton>
              </InputActions>
            </InputRow>
          </ChatInputSection>
        </ModalContent>
      </ModalOverlay>
    ),
    document.body
  );
};

export default CompetitorChatModal;
